import { useEffect, useState } from "react";
import client from "../api/client";
import type { WorkScheduleDay } from "../api/QuikTripClient";

function isOff(day: WorkScheduleDay): boolean {
	return day.shifts.length === 0 || day.shifts.every((s) => s.shiftId === -1);
}

export function SchedulePage() {
	const [weekOffset, setWeekOffset] = useState(0);
	const [currentDays, setCurrentDays] = useState<WorkScheduleDay[]>([]);
	const [futureDays, setFutureDays] = useState<WorkScheduleDay[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setLoading(true);
		client
			.getWorkSchedule()
			.then((s) => {
				setCurrentDays(s.current);
				setFutureDays(s.future);
			})
			.catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
			.finally(() => setLoading(false));
	}, []);

	const days = weekOffset === 0 ? currentDays : futureDays;
	const weekLabel =
		days.length > 0
			? `${days[0].displayDate} – ${days[days.length - 1].displayDate}`
			: weekOffset === 0
				? "This Week"
				: "Next Week";

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<span className="loading loading-spinner loading-md text-primary" />
			</div>
		);
	}

	if (error) {
		return <div className="p-6 text-center text-error text-sm">{error}</div>;
	}

	return (
		<div className="p-3 flex flex-col gap-3">
			<div className="flex items-center justify-between bg-base-200 rounded-xl px-4 py-2">
				<button
					className="btn btn-ghost btn-sm text-primary text-xl px-2 disabled:opacity-30"
					onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
					disabled={weekOffset === 0}
					aria-label="Previous week"
					type="button"
				>
					‹
				</button>
				<span className="font-semibold text-sm">{weekLabel}</span>
				<button
					className="btn btn-ghost btn-sm text-primary text-xl px-2 disabled:opacity-30"
					onClick={() => setWeekOffset((o) => Math.min(1, o + 1))}
					disabled={weekOffset === 1}
					aria-label="Next week"
					type="button"
				>
					›
				</button>
			</div>

			{days.map((day) => {
				const off = isOff(day);
				return (
					<div
						key={day.displayDate}
						className={`bg-base-200 rounded-xl px-4 py-3 border-l-4 ${
							off
								? "border-l-base-300 opacity-50"
								: "border-l-[var(--color-success)]"
						}`}
					>
						{off ? (
							<>
								<div className="font-semibold text-sm">{day.displayDate}</div>
								<div className="text-xs text-base-content/40 mt-0.5">
									Day Off
								</div>
							</>
						) : (
							<div className="flex items-center justify-between">
								<div>
									<div className="font-semibold text-sm">{day.displayDate}</div>
									{day.shifts.map((shift) => (
										<div
											key={shift.shiftId}
											className="text-xs text-base-content/60 mt-0.5"
										>
											{shift.displayTime} · #{day.storeNum}
										</div>
									))}
								</div>
								<div className="flex flex-col gap-1 items-end">
									{day.shifts.map((shift) => (
										<span
											key={shift.shiftId}
											className="badge badge-sm badge-success capitalize"
										>
											{shift.shiftTypeName}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
